import { type CheckedState } from "@radix-ui/react-checkbox"
import { toast } from "react-toastify"
import { trackEvent } from "~/hooks/useTrackingService"
import { api } from "~/utils/api"

import { IconButton } from "../Button"
import { Card, CardContent, CardHeader, CardTitle } from "../Card"
import { Checkbox } from "../Checkbox"
import InformationGrid from "../InformationGrid"
import InformationRow from "../InformationRow"
import LoadingOverlay from "../LoadingOverlay"
import { Popover, PopoverContent, PopoverTrigger } from "../Popover"
import { Info } from "@phosphor-icons/react"
import { getTailwindValue } from "~/utils/tailwind"
import { StatusColor } from "~/constants/status"

interface Props {
  organizationId: string
}

export const EnabledOrgConnectionsCard: React.FC<Props> = ({ organizationId }) => {
  const utils = api.useUtils()
  const { data: connections, isLoading: isLoadingConnections } =
    api.auth0.getAllConnections.useQuery()

  const { data: enabledConnections, isLoading: isLoadingEnabledConnections } =
    api.auth0.getOrganizationConnections.useQuery({
      organizationId,
    })

  const enableConnection = api.auth0.enableOrganizationConnection.useMutation({
    onError: async () => {
      toast.error("Failed to enabled organization connection")
    },
    onSuccess: async () => {
      void utils.auth0.getOrganizationConnections.invalidate({ organizationId })
      trackEvent("Organization Connection Enabled")
      toast.success("Organization connection enabled")
    },
  })

  const disableConnection = api.auth0.disableOrganizationConnection.useMutation({
    onError: async () => {
      toast.error("Failed to disable organization connection")
    },
    onSuccess: async () => {
      void utils.auth0.getOrganizationConnections.invalidate({ organizationId })
      trackEvent("Organization Connection Disabled")
      toast.success("Organization connection disabled")
    },
  })

  const handleConnectionToggle = (connectionId: string, enabled: boolean) => {
    if (enabled) {
      enableConnection.mutate({
        organizationId,
        connectionId,
      })
    } else {
      disableConnection.mutate({
        organizationId,
        connectionId,
      })
    }
  }

  return (
    <Card className="w-full h-fit">
      <CardHeader>
        <CardTitle>Enabled connections</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoadingConnections || isLoadingEnabledConnections ? (
          <LoadingOverlay />
        ) : (
          <div className="flex flex-col gap-2">
            {connections?.map((connection) => {
              const enabledConnection = enabledConnections?.find(
                (con) => con.connection_id === connection.id,
              )

              return (
                <div
                  key={connection.id}
                  className="px-3 py-2 flex flex-row justify-between gap-2 rounded-md shadow-card-rest items-center"
                >
                  <div className="flex flex-row gap-1 items-center">
                    <div className="font-medium text-md">{connection.name}</div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <IconButton variant="transparent" size="sm">
                          <Info
                            color={
                              enabledConnection?.assign_membership_on_login
                                ? getTailwindValue("--color-destructive")
                                : getTailwindValue("--color-fg-muted")
                            }
                          />
                        </IconButton>
                      </PopoverTrigger>
                      <PopoverContent>
                        <InformationGrid>
                          <InformationRow title="ID" value={connection.id} />
                          <InformationRow title="Name" value={connection.name} />
                          <InformationRow
                            title="Strategy"
                            value={connection.strategy}
                            tooltip="The type of the connection, related to the identity provider"
                          />
                          <InformationRow
                            title="Enabled"
                            value={enabledConnection ? "Yes" : "No"}
                          />
                          {enabledConnection && (
                            <>
                              <InformationRow
                                title="Membership on login"
                                value={enabledConnection?.assign_membership_on_login ? "Yes" : "No"}
                                statusColor={
                                  enabledConnection?.assign_membership_on_login
                                    ? StatusColor.Error
                                    : StatusColor.Success
                                }
                                tooltip="Critical that this is not enabled for social connections."
                              />

                              <InformationRow
                                title="Show as button"
                                value={enabledConnection?.show_as_button ? "Yes" : "No"}
                                tooltip="Determines whether a connection should be displayed on this organization’s login prompt. Only applicable for enterprise connections. Default: true."
                              />

                              <InformationRow
                                title="Signup enabled"
                                value={enabledConnection?.is_signup_enabled ? "Yes" : "No"}
                                tooltip="Determines whether organization signup should be enabled for this organization connection. Only applicable for database connections. Default: false."
                              />
                            </>
                          )}
                        </InformationGrid>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <Checkbox
                    requires="staff"
                    checked={!!enabledConnection}
                    onCheckedChange={(value: CheckedState) =>
                      handleConnectionToggle(connection.id, Boolean(value))
                    }
                  />
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
